import { FC, memo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import Badge from '../Badge';
import Dots from '../Dots';
import Tooltip from '../Tooltip';
import Typography from '../Typography';
import {
  IndicatorDot,
  ItemLabel,
  ItemText,
  SidebarMenuItem,
  SidebarSubMenu,
} from './Sidebar.styled';
import { SidebarLink } from './use-get-sidebar-links';

export type SidebarItemProps = SidebarLink & { collapsed?: boolean };

type SideBarLinkProps = Pick<SidebarLink, 'text' | 'icon' | 'unreadCount'> & {
  collapsed: boolean;
};
type SidebarLocation = Pick<ReturnType<typeof useLocation>, 'pathname'>;

export const isSidebarLinkActive = (href: string | null | undefined, location: SidebarLocation) => {
  if (!href) return false;

  // A sidebar link is identified by its pathname alone. Any query string in the href (e.g.
  // `/portfolio?tab=my-assets`) is only a default landing tab, so it must NOT gate the active
  // state — otherwise switching tabs within a section (Portfolio, Deals, Gamma Dashboard) would
  // drop the highlight. This mirrors how `use-header-page` resolves the current section.
  const [pathname] = href.split('?');
  if (!pathname) return false;

  return location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);
};

const SideBarLinkContent: FC<SideBarLinkProps> = ({ text, icon, unreadCount, collapsed }) => (
  <>
    <ItemLabel $collapsed={collapsed}>
      {icon}
      <ItemText $collapsed={collapsed}>
        <Typography variant="body3" fontWeight={500} color="inherit" noWrap>
          {text}
        </Typography>
      </ItemText>
    </ItemLabel>

    {!!unreadCount &&
      (collapsed ? (
        <IndicatorDot>
          <Dots color="green" size="xs" />
        </IndicatorDot>
      ) : (
        <Badge label={unreadCount} color="primary" variant="subtle" size="sm" />
      ))}
  </>
);

export const SidebarItem: FC<SidebarItemProps> = memo(
  ({ text, href, icon, tooltip, onClick, unreadCount, subLinks, collapsed = false }) => {
    const location = useLocation();
    const isActive = isSidebarLinkActive(href, location);
    const content = (
      <SideBarLinkContent text={text} icon={icon} unreadCount={unreadCount} collapsed={collapsed} />
    );

    return (
      <>
        <SidebarMenuItem active={isActive} disabled={!href} $collapsed={collapsed}>
          {href ? (
            <Tooltip title={text} side="right" disabled={!collapsed}>
              <RouterLink to={href} onClick={onClick}>
                {content}
              </RouterLink>
            </Tooltip>
          ) : (
            <Tooltip title={tooltip} side="right" disabled={!tooltip}>
              <div aria-disabled="true">{content}</div>
            </Tooltip>
          )}
        </SidebarMenuItem>

        {!collapsed && !!subLinks?.length && (
          <SidebarSubMenu>
            {subLinks.map((subLink) => (
              <SidebarItem key={subLink.text} {...subLink} />
            ))}
          </SidebarSubMenu>
        )}
      </>
    );
  },
);

SidebarItem.displayName = 'SidebarItem';
